import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { formatDashSeparator } from 'LuxKit/intl/separators'
import styled from 'styled-components'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import PlaceImageLoader from 'tripPlanner/components/Chat/PlaceImageLoader'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { isHotelSavedData, isSppSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import TextButton from 'LuxKit/Button/TextButton'
import DayItineraryCanvas from './DayItineraryCanvas'
import ChatItemDropdown from './ChatItemDropdown'
import noop from 'lib/function/noop'
import ModalContext from 'contexts/ModalContext'
import ConfirmDeleteStopModal from './ConfirmDeleteStopModal'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Counter, { type CounterChangeEventHandler } from 'LuxKit/Counter'
import ItineraryLoadingState from './ItineraryLoadingState'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import type { HotelMapViewData, ExperienceMapViewData, TripItineraryData } from 'tripPlanner/api/types'
import PlaceInfoDrawer from './PlaceInfoDrawer'
import { HotelListingDrawer } from './HotelListingDrawer'
import type { DrawerHotel } from './HotelListingDrawer'
import { ExperienceListingDrawer } from './ExperienceListingDrawer'
import ChatOfferDetailDrawer from './ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from './ChatExperienceDetailDrawer'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import productLabels from 'i18n/productLabels'
import { buildDeleteItemMessage, buildDeleteStopMessage } from '../utils/deleteTripMessages'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import type { DrawerExperience } from './ExperienceListingDrawer/ExperienceDetailView'
import type { OfferClickSearchParams } from '../contexts/ChatMessageActionsContext'
import { useSortedHighlights } from '../hooks/useSortedHighlights'
import type { Highlight } from './TripPlanCanvas'
import Divider from 'LuxKit/Divider'
import type { MapCoordinate } from 'tripPlanner/utils/mapMarkerTransforms'
import type { LEExploreCategorySource } from 'tripPlanner/pages/chat/chatExploreCategories'

interface Props {
  data?: TripItineraryData
  stopId: number
  placeName: string
  placeCountry: string
  numberOfDays: number
  description: string
  imageId?: string
  stopSeq: number
  highlights?: Array<Highlight>
  googlePlaceId?: string
  onLearnMoreClick?: (data: { googlePlaceId: string; placeName: string; countryName: string; stopId?: number }) => void
  onSendMessage?: (message: string) => void
  onExploreMap: (coordinates: MapCoordinate, source: LEExploreCategorySource) => void
  onShowMap: (data: HotelMapViewData) => void
  onShowExperienceOnMap?: (data: ExperienceMapViewData) => void
  stopCoordinates?: MapCoordinate
  isEnriching?: boolean
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
  scrollTargetDayId?: number
  scrollTargetItemId?: number
}

const StopSheet = styled(Sheet)`
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.L};
`

const StopSheetContent = styled(Group)`
  padding: var(--itinerary-stop-sheet-padding, ${rem(20)});
`

const StyledTextButton = styled(TextButton)`
  padding-left: 0;
`

const ImageContainer = styled(Clickable)`
  width: var(--itinerary-stop-image-size, ${rem(136)});
  height: var(--itinerary-stop-image-size, ${rem(136)});
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  flex-shrink: 0;
`

const StopMainContent = styled(Group)`
  min-width: 0;
`

const StopTitleContent = styled(Group)`
  min-width: 0;
`

const ClickableHeading = styled(Clickable)`
  &:hover {
    text-decoration: underline;
  }
`

const ClickableHighlightTitle = styled(Clickable)`
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }
`

function TripPlanCanvasItem({
  data,
  stopId,
  placeName,
  placeCountry,
  numberOfDays,
  description,
  imageId,
  highlights,
  googlePlaceId,
  onLearnMoreClick = noop,
  onSendMessage = noop,
  onExploreMap,
  onShowMap,
  onShowExperienceOnMap,
  stopCoordinates,
  isEnriching = false,
  onAskAI,
  scrollTargetDayId,
  scrollTargetItemId,
}: Props) {
  const intl = useIntl()
  const { isStreaming } = useChatStreamStatus()
  const [isCreateItineraryOpen, setIsCreateItineraryOpen] = useState(false)
  const [isUpdateItineraryOpen, setIsUpdateItineraryOpen] = useState(false)
  const [itineraryDays, setItineraryDays] = useState<number>(1)
  const [updateItineraryDays, setUpdateItineraryDays] = useState<number>(1)
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false)
  const [requestedDaysCount, setRequestedDaysCount] = useState<number>(0)
  const [isHotelDrawerOpen, setIsHotelDrawerOpen] = useState<boolean>(false)
  const [isExperienceDrawerOpen, setIsExperienceDrawerOpen] = useState<boolean>(false)
  const [selectedHighlight, setSelectedHighlight] = useState<{ googlePlaceId: string; name: string; itemId: number } | undefined>(undefined)
  const [selectedHotelHighlight, setSelectedHotelHighlight] = useState<{
    offerId: string
    accommodationPropertyId?: string
    offerType?: string
    searchParams?: OfferClickSearchParams
  } | undefined>(undefined)
  const createItineraryTriggerRef = useRef<HTMLButtonElement>(null)
  const updateItineraryTriggerRef = useRef<HTMLButtonElement>(null)

  const showModal = useContext(ModalContext)

  const currentStop = useMemo(() => {
    return data?.stops.find(stop => stop.id === stopId)
  }, [data, stopId])

  const actualDaysCount = currentStop?.days?.length ?? 0
  const hasDays = actualDaysCount > 0
  const hasItemsWithoutDay = (currentStop?.itemsWithoutDay?.length ?? 0) > 0
  const hasHighlights = (highlights?.length ?? 0) > 0

  useEffect(() => {
    if (isGeneratingItinerary && hasDays) {
      setIsGeneratingItinerary(false)
    }
  }, [isGeneratingItinerary, hasDays])

  useEffect(() => {
    if (isGeneratingItinerary && !isStreaming && !hasDays) {
      setIsGeneratingItinerary(false)
    }
  }, [isGeneratingItinerary, isStreaming, hasDays])

  useEffect(() => {
    if (requestedDaysCount > 0 && hasDays && !isGeneratingItinerary) {
      setRequestedDaysCount(0)
    }
  }, [hasDays, isGeneratingItinerary, requestedDaysCount])

  useEffect(() => {
    if (actualDaysCount > 0) {
      setUpdateItineraryDays(actualDaysCount)
    }
  }, [actualDaysCount])

  const onLearnMoreClickHandler = useCallback(() => {
    if (googlePlaceId) {
      onLearnMoreClick({ googlePlaceId, placeName, countryName: placeCountry, stopId })
    }
  }, [onLearnMoreClick, googlePlaceId, placeName, placeCountry, stopId])

  const toggleCreateItineraryDropdown = useCallback(() => {
    setIsCreateItineraryOpen(prev => !prev)
  }, [])

  const closeCreateItineraryDropdown = useCallback(() => {
    setIsCreateItineraryOpen(false)
  }, [])

  const onItineraryDaysChange = useCallback<CounterChangeEventHandler>((value) => {
    setItineraryDays(value)
  }, [])

  const handleCreateItinerary = useCallback((days?: number) => {
    const daysToUse = days ?? itineraryDays
    closeCreateItineraryDropdown()
    setIsGeneratingItinerary(true)
    setRequestedDaysCount(daysToUse)
    const displayDaysLabel = intl.formatMessage(
      {
        id: 'WuQGcs5pleJG0thhNUz_',
        defaultMessage: '{count, plural, one {# day} other {# days}}',
        description: 'Day count for itinerary creation prompt on the itinerary or schedule view',
      },
      { count: daysToUse },
    )
    const contextDaysLabel = daysToUse === 1 ? '1 day' : `${daysToUse} days`
    const displayMessage = intl.formatMessage(
      {
        id: 'qW6V5zYOF73N6_jtHmYX',
        defaultMessage: 'Create a full, {daysLabel} itinerary for {placeName}',
        description: 'User chat message shown when creating a full itinerary for a place with a chosen day count',
      },
      {
        daysLabel: displayDaysLabel,
        placeName,
      },
    )
    onSendMessage(
      appendContextTag(
        displayMessage,
        `Create a full, ${contextDaysLabel} itinerary for ${placeName}`,
        TRIP_PLANNER_ACTION_CONTEXT_LABEL,
      ),
    )
  }, [intl, closeCreateItineraryDropdown, itineraryDays, onSendMessage, placeName])

  const handleCreateItineraryWithNumberOfDays = useCallback(() => {
    if (numberOfDays > 0) {
      handleCreateItinerary(numberOfDays)
    }
  }, [numberOfDays, handleCreateItinerary])

  const handleCreateItineraryFromDropdown = useCallback(() => {
    handleCreateItinerary()
  }, [handleCreateItinerary])

  const toggleUpdateItineraryDropdown = useCallback(() => {
    setIsUpdateItineraryOpen(prev => !prev)
  }, [])

  const closeUpdateItineraryDropdown = useCallback(() => {
    setIsUpdateItineraryOpen(false)
  }, [])

  const onUpdateItineraryDaysChange = useCallback<CounterChangeEventHandler>((value) => {
    setUpdateItineraryDays(value)
  }, [])

  const handleUpdateItinerary = useCallback(() => {
    closeUpdateItineraryDropdown()
    setIsGeneratingItinerary(true)
    setRequestedDaysCount(updateItineraryDays)
    const displayDaysLabel = intl.formatMessage(
      {
        id: 'VGqAsSYRfzb6pjdK23dd',
        defaultMessage: '{count, plural, one {# day} other {# days}}',
        description: 'Day count for itinerary update prompt in the date selection or calendar picker',
      },
      { count: updateItineraryDays },
    )
    const contextDaysLabel = updateItineraryDays === 1 ? '1 day' : `${updateItineraryDays} days`
    const displayMessage = intl.formatMessage(
      {
        id: 'zOkcbbPoJRCjf9TyIrkk',
        defaultMessage: 'Update my {placeName} itinerary to be {daysLabel}',
        description: 'User chat message shown when updating an itinerary to a new total number of days',
      },
      {
        placeName,
        daysLabel: displayDaysLabel,
      },
    )
    onSendMessage(
      appendContextTag(
        displayMessage,
        `Update my ${placeName} itinerary to be ${contextDaysLabel}`,
        TRIP_PLANNER_ACTION_CONTEXT_LABEL,
      ),
    )
  }, [intl, closeUpdateItineraryDropdown, updateItineraryDays, onSendMessage, placeName])

  const handleDelete = useCallback(async() => {
    const confirmed = await showModal<boolean>(<ConfirmDeleteStopModal placeName={placeName} />)
    if (!confirmed) return
    onSendMessage(buildDeleteStopMessage({ intl, stop: { id: stopId, placeName } }))
  }, [showModal, placeName, stopId, onSendMessage, intl])

  const handleOpenHotelDrawer = useCallback(() => {
    setIsHotelDrawerOpen(true)
  }, [])

  const handleCloseHotelDrawer = useCallback(() => {
    setIsHotelDrawerOpen(false)
  }, [])

  const handleOpenExperienceDrawer = useCallback(() => {
    setIsExperienceDrawerOpen(true)
  }, [])

  const handleCloseExperienceDrawer = useCallback(() => {
    setIsExperienceDrawerOpen(false)
  }, [])

  const handleShowMap = useCallback((hotel: DrawerHotel) => {
    handleCloseHotelDrawer()
    const mapViewData: HotelMapViewData = {
      searchQuery: placeName,
      offers: [{
        id: hotel.id,
        name: hotel.name,
        coordinates: hotel.geoLocation!,
      }],
    }
    onShowMap(mapViewData)
  }, [handleCloseHotelDrawer, placeName, onShowMap])

  const handleExploreMap = useCallback(() => {
    handleCloseHotelDrawer()
    if (stopCoordinates) {
      onExploreMap(stopCoordinates, 'le-hotels')
    }
  }, [handleCloseHotelDrawer, onExploreMap, stopCoordinates])

  const handleExploreExperienceMap = useCallback(() => {
    handleCloseExperienceDrawer()
    if (stopCoordinates) {
      onExploreMap(stopCoordinates, 'le-experiences')
    }
  }, [handleCloseExperienceDrawer, onExploreMap, stopCoordinates])

  const handleShowExperienceOnMap = useCallback((experience: { offerId: string; name: string; geoLocation: { lat: number; lng: number } }) => {
    handleCloseExperienceDrawer()
    onShowExperienceOnMap?.({
      searchQuery: experience.name,
      offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
    })
  }, [handleCloseExperienceDrawer, onShowExperienceOnMap])

  const handleHighlightClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { googlePlaceId: highlightGooglePlaceId, name, itemId } = event.currentTarget.dataset
    if (highlightGooglePlaceId && name && itemId) {
      setSelectedHighlight({ googlePlaceId: highlightGooglePlaceId, name, itemId: Number(itemId) })
    }
  }, [])

  const handleCloseHighlightDrawer = useCallback(() => {
    setSelectedHighlight(undefined)
  }, [])

  const handleCloseHotelHighlightDrawer = useCallback(() => {
    setSelectedHotelHighlight(undefined)
  }, [])

  const handleDeleteHighlightItem = useCallback((itemId: number) => {
    const highlight = highlights?.find((h) => h.id === itemId)
    if (!highlight) return
    onSendMessage(buildDeleteItemMessage({
      intl,
      item: { id: highlight.id, name: highlight.name },
      stop: { id: stopId, placeName },
    }))
    setSelectedHighlight(undefined)
  }, [highlights, intl, onSendMessage, placeName, stopId])

  const handleShowExperienceHighlightOnMap = useCallback((experience: DrawerExperience) => {
    handleCloseHotelHighlightDrawer()
    if (!experience.geoLocation) return
    onShowExperienceOnMap?.({
      searchQuery: experience.name,
      offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
    })
  }, [handleCloseHotelHighlightDrawer, onShowExperienceOnMap])

  const { sortedHighlights } = useSortedHighlights(highlights)

  const hotelHighlightHandlers = useMemo(() => {
    const handlers: Record<number, () => void> = {}

    for (const highlight of sortedHighlights) {
      if (!highlight.googlePlaceId && highlight.savedItemData) {
        const savedData = highlight.savedItemData

        if (isHotelSavedData(savedData)) {
          const searchParams: OfferClickSearchParams = {}
          if (savedData.checkIn) searchParams.checkIn = savedData.checkIn
          if (savedData.checkOut) searchParams.checkOut = savedData.checkOut

          handlers[highlight.id] = () => setSelectedHotelHighlight({
            offerId: savedData.offerId,
            accommodationPropertyId: isSppSavedData(savedData) ? savedData.accommodationPropertyId : undefined,
            offerType: savedData.offerType,
            searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
          })
        } else if (savedData.source === 'offer_experience') {
          handlers[highlight.id] = () => setSelectedHotelHighlight({
            offerId: savedData.experienceId,
            offerType: 'experience',
          })
        }
      }
    }

    return handlers
  }, [sortedHighlights])

  const placeNameHeading = googlePlaceId ? (
    <ClickableHeading onClick={onLearnMoreClickHandler}>
      <Heading variant="heading3">{placeName}</Heading>
    </ClickableHeading>
  ) : (
    <Heading variant="heading3">{placeName}</Heading>
  )

  const learnMoreLabel = intl.formatMessage({
    id: '54YyE2EorGEGt1ZOGM1l',
    defaultMessage: 'Learn more',
    description: 'Button label to learn more about a trip destination',
  })

  const hotelsLabel = intl.formatMessage(productLabels.hotels)

  const experiencesLabel = intl.formatMessage({
    id: 'kRX_WqaThWzGMUBgBGly',
    defaultMessage: 'Experiences',
    description: 'Clickable list item title to view experiences for a trip stop',
  })

  const itineraryHeadingLabel = intl.formatMessage({
    id: 'RUICEpJ3xQuhVvPQHvCG',
    defaultMessage: 'Itinerary',
    description: 'Section heading for the trip stop itinerary in the trip planner',
  })

  const highlightsHeadingLabel = intl.formatMessage({
    id: 'vCnI0fSFo0TfSafwS2AE',
    defaultMessage: 'Highlights',
    description: 'Section heading for the trip stop highlights in the trip planner',
  })

  const createItineraryLabel = intl.formatMessage({
    id: 'FKx4t_HttTBgdfFBFUcB',
    defaultMessage: 'Create itinerary',
    description: 'Button label to create an itinerary for a trip stop',
  })

  const createLabel = intl.formatMessage({
    id: '4XOf32wU7_mbMlGZiToJ',
    defaultMessage: 'Create',
    description: 'Primary action label in the create itinerary dropdown',
  })

  const updateLabel = intl.formatMessage({
    id: 'Q6e2-GDA25X3KXgFqqqw',
    defaultMessage: 'Update',
    description: 'Primary action label in the update itinerary dropdown',
  })

  const showHighlightFallback = !hasDays && hasHighlights
  const sectionHeadingLabel = showHighlightFallback ? highlightsHeadingLabel : itineraryHeadingLabel

  return (
    <>
      <StopSheet border="light-foreground">
        <StopSheetContent direction="vertical" gap={20}>
          <Group direction="horizontal" gap={16} verticalAlign="stretch">
            {(!!imageId || isEnriching) && (
              <ImageContainer onClick={googlePlaceId ? onLearnMoreClickHandler : undefined}>
                {imageId && <ResponsiveImage aspectRatio="1:1" id={imageId} fit="center" alt={placeName} />}
                {!imageId && <PlaceImageLoader itemId={stopId} />}
              </ImageContainer>
            )}
            <StopMainContent direction="vertical" gap={12} fullWidth>
              <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="start" fullWidth>
                <StopTitleContent direction="vertical" gap={4}>
                  {placeNameHeading}
                  <BodyText variant="medium">{placeCountry}</BodyText>
                </StopTitleContent>
                <Group direction="horizontal" gap={8} verticalAlign="start">
                  <ChatItemDropdown onDelete={handleDelete} />
                </Group>
              </Group>
              <BodyText variant="medium">
                {googlePlaceId && <FormattedMessage
                  id="19jahUwozRb-OsGlmpIw"
                  defaultMessage="{description} {learnMoreButton}"
                  description="Place description followed by a learn more link in the trip planner AI chat"
                  values={{
                    description,
                    learnMoreButton: (
                      <StyledTextButton size="medium" kind="tertiary" onClick={onLearnMoreClickHandler}>{learnMoreLabel}</StyledTextButton>
                    ),
                  }}
                />}
                {!googlePlaceId && description}
              </BodyText>
            </StopMainContent>
          </Group>
          <Group direction="vertical" gap={8}>
            <ClickableListItem
              size="medium"
              startIcon={<LineBedIcon />}
              endIcon={<LineAngleRightIcon />}
              title={hotelsLabel}
              onClick={handleOpenHotelDrawer}
            />
            <Divider kind="secondary" />
            <ClickableListItem
              size="medium"
              startIcon={<LineTicketIcon />}
              endIcon={<LineAngleRightIcon />}
              title={experiencesLabel}
              onClick={handleOpenExperienceDrawer}
            />
          </Group>
          {(hasHighlights || hasDays || hasItemsWithoutDay) && (
            <Sheet padding={16} border="light-foreground" borderRadius="L">
              <Group direction="vertical" gap={8}>
                <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between">
                  <BodyText variant="large" weight="bold">{sectionHeadingLabel}</BodyText>
                  {!hasDays && !isGeneratingItinerary && (
                    <>
                      <TextButton
                        size="medium"
                        kind="primary"
                        shape="round"
                        startIcon={<SolidSparklesIcon />}
                        onClick={numberOfDays > 0 ? handleCreateItineraryWithNumberOfDays : toggleCreateItineraryDropdown}
                        ref={createItineraryTriggerRef}
                        disabled={isStreaming}
                      >
                        {createItineraryLabel}
                      </TextButton>
                      {!(numberOfDays > 0) && (
                        <DropdownList
                          size="S"
                          open={isCreateItineraryOpen}
                          onClose={closeCreateItineraryDropdown}
                          triggerRef={createItineraryTriggerRef}
                          anchorRef={createItineraryTriggerRef}
                          placement="bottom-end"
                          portaled
                          primaryActionProps={{ children: createLabel, onClick: handleCreateItineraryFromDropdown }}
                          footerActionsLayout="stacked"
                          enableFlip
                        >
                          <Sheet padding={16}>
                            <Group direction="vertical" gap={8} horizontalAlign="start">
                              <BodyText variant="medium">
                                {intl.formatMessage(
                                  {
                                    id: 'CsC8uRppEP6LyE7SIfDU',
                                    defaultMessage: 'How many days would you like to spend in <strong>{placeName}</strong>?',
                                    description: 'Body text asking user to choose number of days for creating an itinerary',
                                  },
                                  { placeName, strong: (chunks: React.ReactNode) => <strong>{chunks}</strong> },
                                )}
                              </BodyText>
                              <Counter
                                value={itineraryDays}
                                min={1}
                                max={365}
                                onChange={onItineraryDaysChange}
                                size="large"
                              />
                            </Group>
                          </Sheet>
                        </DropdownList>
                      )}
                    </>
                  )}
                  {hasDays && (
                    <>
                      <TextButton
                        size="medium"
                        kind="secondary"
                        shape="round"
                        variant="dark"
                        onClick={toggleUpdateItineraryDropdown}
                        ref={updateItineraryTriggerRef}
                        endIcon={<LineAngleDownIcon />}
                        disabled={isStreaming}
                      >
                        {intl.formatMessage(
                          {
                            id: 'v8Tu4UNPoKnE4S1aErdp',
                            defaultMessage: '{count, plural, one {# day} other {# days}}',
                            description: 'Button label showing the number of days in the itinerary for a trip stop',
                          },
                          { count: actualDaysCount },
                        )}
                      </TextButton>
                      <DropdownList
                        size="S"
                        open={isUpdateItineraryOpen}
                        onClose={closeUpdateItineraryDropdown}
                        triggerRef={updateItineraryTriggerRef}
                        anchorRef={updateItineraryTriggerRef}
                        placement="bottom-end"
                        portaled
                        primaryActionProps={{ children: updateLabel, onClick: handleUpdateItinerary }}
                        footerActionsLayout="stacked"
                        enableFlip
                      >
                        <Sheet padding={16}>
                          <Group direction="vertical" gap={8} horizontalAlign="start">
                            <BodyText variant="medium">
                              {intl.formatMessage(
                                {
                                  id: 'ZElMrD9CmiTsm6MO_t2S',
                                  defaultMessage: 'How many days would you like to spend in <strong>{placeName}</strong>?',
                                  description: 'Body text asking user to choose number of days for updating an itinerary',
                                },
                                { placeName, strong: (chunks: React.ReactNode) => <strong>{chunks}</strong> },
                              )}
                            </BodyText>
                            <Counter
                              value={updateItineraryDays}
                              min={1}
                              max={365}
                              onChange={onUpdateItineraryDaysChange}
                              size="large"
                            />
                          </Group>
                        </Sheet>
                      </DropdownList>
                    </>
                  )}
                </Group>
                {showHighlightFallback && !isGeneratingItinerary && (
                  <Group direction="vertical" gap={8}>
                    {sortedHighlights.map((highlight) => (
                      <Group key={`${highlight.id}-${highlight.name}`} direction="horizontal" gap={8} verticalAlign="start">
                        <BodyText variant="medium" as="span">
                          {highlight.emoji}
                        </BodyText>
                        <BodyText variant="medium">
                          {highlight.googlePlaceId && (
                            <ClickableHighlightTitle
                              data-google-place-id={highlight.googlePlaceId}
                              data-name={highlight.name}
                              data-item-id={highlight.id}
                              onClick={handleHighlightClick}
                            >
                              {highlight.name}
                            </ClickableHighlightTitle>
                          )}
                          {!highlight.googlePlaceId && hotelHighlightHandlers[highlight.id] && (
                            <ClickableHighlightTitle onClick={hotelHighlightHandlers[highlight.id]}>
                              {highlight.name}
                            </ClickableHighlightTitle>
                          )}
                          {!highlight.googlePlaceId && !hotelHighlightHandlers[highlight.id] && <b>{highlight.name}</b>}
                          {formatDashSeparator(intl)}{highlight.description}
                        </BodyText>
                      </Group>
                    ))}
                  </Group>
                )}
                {isGeneratingItinerary && !hasDays && (
                  <ItineraryLoadingState daysCount={requestedDaysCount} />
                )}
                {(hasDays || (hasItemsWithoutDay && !showHighlightFallback)) && (
                  <DayItineraryCanvas
                    data={data}
                    stopId={stopId}
                    onSendMessage={onSendMessage}
                    onShowMap={handleShowMap}
                    onShowExperienceOnMap={onShowExperienceOnMap}
                    onAskAI={onAskAI}
                    scrollTargetDayId={scrollTargetDayId}
                    scrollTargetItemId={scrollTargetItemId}
                  />
                )}
              </Group>
            </Sheet>
          )}
        </StopSheetContent>
      </StopSheet>
      {selectedHighlight && (
        <PlaceInfoDrawer
          isOpen
          onClose={handleCloseHighlightDrawer}
          googlePlaceId={selectedHighlight.googlePlaceId}
          placeName={selectedHighlight.name}
          itemId={selectedHighlight.itemId}
          onDelete={handleDeleteHighlightItem}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}
      <HotelListingDrawer
        isOpen={isHotelDrawerOpen}
        onClose={handleCloseHotelDrawer}
        placeName={placeName}
        placeCountry={placeCountry}
        stopStartDate={currentStop?.startDate}
        stopEndDate={currentStop?.endDate}
        stopCoordinates={stopCoordinates}
        contextStop={currentStop}
        onShowMap={handleShowMap}
        onExploreMap={stopCoordinates ? handleExploreMap : undefined}
        itineraryData={data}
        onSendMessage={onSendMessage}
      />
      <ExperienceListingDrawer
        isOpen={isExperienceDrawerOpen}
        onClose={handleCloseExperienceDrawer}
        placeName={placeName}
        placeCountry={placeCountry}
        stopCoordinates={stopCoordinates}
        onExploreMap={stopCoordinates ? handleExploreExperienceMap : undefined}
        itineraryData={data}
        onSendMessage={onSendMessage}
        onShowExperienceOnMap={handleShowExperienceOnMap}
      />
      {selectedHotelHighlight?.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          offerId={selectedHotelHighlight.offerId}
          isOpen
          onClose={handleCloseHotelHighlightDrawer}
          onShowMap={handleShowExperienceHighlightOnMap}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}
      {selectedHotelHighlight && selectedHotelHighlight.offerType !== 'experience' && (
        <ChatOfferDetailDrawer
          offerId={selectedHotelHighlight.offerId}
          accommodationPropertyId={selectedHotelHighlight.accommodationPropertyId}
          offerType={selectedHotelHighlight.offerType}
          isOpen
          onClose={handleCloseHotelHighlightDrawer}
          onShowMap={handleShowMap}
          itineraryData={data}
          onSendMessage={onSendMessage}
          searchParams={selectedHotelHighlight.searchParams}
        />
      )}
    </>
  )
}

export default TripPlanCanvasItem
